import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faExpandArrowsAlt, faCompressArrowsAlt } from '@fortawesome/free-solid-svg-icons'
import Sidebar from './Sidebar'
import NotificationBell from '../../components/NotificationBell'
import { useUI } from '../../contexts/UIContext'
import { useFullscreen } from '../../contexts/screen-context'
import { useAuth } from '../../contexts/auth-context'
import './Header.css'

// Same env var the auth context uses to identify the staffing-app parent.
const STAFFING_APP_URL =
  import.meta.env.VITE_STAFFING_APP_URL || 'https://www.staffing.edc.design'

// True when SC is rendered inside the staffing-app iframe (vs opened
// standalone). Cross-origin window.top access throws — treat that as
// "embedded" too.
const isEmbedded = () => {
  try {
    return window.top !== window.self
  } catch (_) {
    return true
  }
}

const Header = () => {
  const { showSidebar, setShowSidebar } = useUI()
  const [showTitle, setShowTitle] = useState('Home')
  const { isAuthenticated } = useAuth()
  const { isFullscreen, setIsFullscreen } = useFullscreen()
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  // Track window width for responsive adjustments
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar)
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const embedded = isEmbedded()
  const goBackToHub = () => {
    try {
      window.parent.postMessage({ type: 'SC_BACK_TO_HUB' }, STAFFING_APP_URL)
    } catch (_) {
      // Parent unreachable — fall back to a top-level redirect so the
      // user isn't stranded inside the iframe.
      try { window.top.location.href = STAFFING_APP_URL } catch (__) {}
    }
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <>
      <header className="main-header mt-2 flex justify-between items-center bg-gray-700/[0.8] rounded-lg px-4 py-2 shadow-lg h-12 relative z-[9999]">
        <div className="flex items-center space-x-3">
          <button
            onClick={toggleSidebar}
            className="header-button text-white hover:text-blue-300"
            aria-label="Toggle Sidebar"
          >
            <FontAwesomeIcon icon={faBars} className="icon text-xl" />
          </button>
          <span className="header-title text-2xl text-white font-semibold tracking-wide">{showTitle}</span>
        </div>

        <Sidebar showSidebar={showSidebar} setShowSidebar={setShowSidebar} setShowTitle={setShowTitle} />

        <div className="flex items-center space-x-2">
          {embedded && (
            <button
              onClick={goBackToHub}
              className="header-button text-white hover:text-blue-300 text-sm font-semibold tracking-wider px-3"
              aria-label="Back to EDC Hub"
              title="Back to EDC Hub"
            >
              EDC HUB
            </button>
          )}
          {/* Notification Bell */}
          <NotificationBell />

          {/* Only show fullscreen button if not on mobile */}
          {windowWidth > 768 && (
            <button
              onClick={toggleFullscreen}
              className="header-button text-white hover:text-blue-300"
              aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            >
              <FontAwesomeIcon icon={isFullscreen ? faCompressArrowsAlt : faExpandArrowsAlt} className="icon text-xl" />
            </button>
          )}
        </div>
      </header>
    </>
  )
}

export default Header
